import React, {useCallback, useState} from 'react';
import {StyleSheet} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import type {DevicesStackParamList} from '@/app/routes';
import {DeviceCard} from '@/features/view-devices/ui/DeviceCard';
import {DeviceList} from '@/features/view-devices/ui/DeviceList';
import {DeviceToolsSheet} from '@/features/manage-device/ui/DeviceToolsSheet';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppButton, IconButton} from '@/shared/ui/button';
import {Page} from '@/shared/ui/page';
import {ContextPlaceholder} from '@/widgets/context-placeholder/ContextPlaceholder';

/**
 * «Устройства» — терминалы и всё, что стоит у двери: турникеты, контроллеры,
 * десктоп-мосты. Подключение по QR и пакетные операции — здесь же.
 *
 * Камеры отсюда уехали в свою вкладку «Камеры» (владелец, 2026-10-22): просмотр —
 * отдельное рабочее место, и сегмент «Терминалы / Камеры» внутри этого раздела был
 * лишним нажатием каждый раз. Список устройств камер и так не показывал
 * (`DeviceList` их отсеивает): их состояние — в просмотре, по сокету.
 */
type Props = NativeStackScreenProps<DevicesStackParamList, 'DeviceList'>;

const styles = StyleSheet.create({
  grow: {flex: 1},
});

export function DevicesScreen({navigation}: Props) {
  const {t} = useTranslation();
  const {isDesktop} = useBreakpoint();
  const companyId = useAuthStore(s => s.activeContext?.companyId);
  const [tools, setTools] = useState(false);

  // Стабильные ссылки для мемоизированных строк — объявлены выше раннего возврата.
  const openDevice = useCallback(
    (deviceId: string, name: string) => navigation.navigate('Device', {deviceId, name}),
    [navigation],
  );
  const openAdopt = useCallback(() => navigation.navigate('ScanDevice'), [navigation]);
  const openTools = useCallback(() => setTools(true), []);

  if (!companyId) {
    return <ContextPlaceholder titleKey="nav.devices" />;
  }

  // Прокрутка одна и принадлежит странице: список постраничный, а инспектор справа
  // катается сам.
  return (
    <Page
      scroll
      gutter={false}
      title={t('nav.devices')}
      testID="devices-screen"
      // Глаголы страницы — в шапке. «Подключить» — залитый, ради него экран и
      // существует; пакетные операции — обводкой.
      actions={
        <>
          {/* На телефоне второстепенный глагол — значком: две полные кнопки в 390pt
              не помещаются в строку и встают друг под другом. */}
          {isDesktop ? (
            <AppButton
              title={t('deviceBulk.title')}
              icon="bulk"
              tone="outline"
              size="md"
              onPress={openTools}
              testID="device-tools-open"
            />
          ) : (
            <IconButton
              icon="bulk"
              tone="outline"
              size={44}
              label={t('deviceBulk.title')}
              onPress={openTools}
              testID="device-tools-open"
            />
          )}
          <AppButton
            title={t('home.adopt.cta')}
            icon="qr"
            size={isDesktop ? 'md' : 'lg'}
            onPress={openAdopt}
            style={isDesktop ? undefined : styles.grow}
            testID="device-adopt"
          />
        </>
      }>
      <DeviceList
        companyId={companyId}
        onOpen={openDevice}
        onAdopt={openAdopt}
        // На широком окне карточка терминала открывается рядом с таблицей, с
        // живым переключателем режима прохода прямо в панели.
        renderDetail={(deviceId, name) => (
          <DeviceCard
            companyId={companyId}
            deviceId={deviceId}
            onOpenSettings={() =>
              navigation.navigate('DeviceSettings', {deviceId, name})
            }
          />
        )}
      />
      <DeviceToolsSheet
        companyId={companyId}
        visible={tools}
        onClose={() => setTools(false)}
      />
    </Page>
  );
}
